import {
  applyPwaRecovery,
  type PwaRecoveryEnvironment,
  type PwaRecoveryWorker,
} from '@forma/pwa-recovery';

const defaultCheckIntervalMs = 15 * 60 * 1000;
const serviceWorkerMessageTimeoutMs = 2000;

function readBrowserStorage(key: 'localStorage' | 'sessionStorage') {
  try {
    return window[key];
  } catch {
    return undefined;
  }
}

function messageServiceWorker(
  worker: PwaRecoveryWorker,
  message: unknown,
): Promise<void> {
  if (!isServiceWorker(worker) || typeof MessageChannel === 'undefined') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const channel = new MessageChannel();
    const timeoutId = window.setTimeout(resolve, serviceWorkerMessageTimeoutMs);

    channel.port1.onmessage = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };

    worker.postMessage(message, [channel.port2]);
  });
}

function isServiceWorker(worker: unknown): worker is ServiceWorker {
  return (
    typeof worker === 'object' &&
    worker !== null &&
    'postMessage' in worker &&
    typeof worker.postMessage === 'function'
  );
}

function createViteRecoveryEnvironment(): PwaRecoveryEnvironment | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return {
    caches: 'caches' in window ? window.caches : undefined,
    fetch: window.fetch.bind(window),
    getServiceWorkerRegistration:
      'serviceWorker' in window.navigator
        ? async () =>
            (await window.navigator.serviceWorker.getRegistration()) ?? null
        : undefined,
    localStorage: readBrowserStorage('localStorage'),
    messageServiceWorker,
    reload: () => window.location.reload(),
    sessionStorage: readBrowserStorage('sessionStorage'),
  };
}

export function startClientRecoveryChecks({
  intervalMs = defaultCheckIntervalMs,
}: { intervalMs?: number } = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  let lastCheckAt = 0;
  let running = false;

  const maybeCheck = () => {
    const now = Date.now();

    if (running || now - lastCheckAt < intervalMs) {
      return;
    }

    running = true;
    lastCheckAt = now;
    const environment = createViteRecoveryEnvironment();
    if (!environment) {
      running = false;
      return;
    }
    void applyPwaRecovery(environment).finally(() => {
      running = false;
    });
  };

  maybeCheck();
  window.addEventListener('focus', maybeCheck);
  window.addEventListener('online', maybeCheck);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      maybeCheck();
    }
  });
}
